import type { SideMenuItem } from './SideMenu';

const base = '/home/taxreturns';
/** Every item carries an icon: a collapsed menu shows icons only (design review 2 Nov), so an item without one would vanish. */
const item = (path: string, icon: string, label: string): SideMenuItem => ({ to: `${base}/${path}`, icon, label });

/**
 * APT348: Nominal Mapping, Journals, Adjusted Trial Balance and Working Papers are one section, "Adjustments & Reports". Its
 * menu entry opens the first of them and stays highlighted on all four; each of the four shows the same top navigation
 * (AdjustmentsReportsNav) in the client's order. The routes themselves are unchanged.
 */
export const ADJUSTMENTS_REPORTS_LABEL = 'Adjustments & Reports';
export const ADJUSTMENTS_REPORTS_ITEMS: SideMenuItem[] = [
  item('tasks-nominalmapping', 'account_tree', 'Nominal Mapping'),
  item('tasks-journals', 'menu_book', 'Journals'),
  item('tasks-adjustedtrialbalance', 'table_chart', 'Adjusted Trial Balance'),
  item('working-papers', 'fact_check', 'Working Papers'),
];

/**
 * APT353 (APT358 renamed Statutory Database to Statutory Disclosures): Statutory Disclosures, Validation Centre, iXBRL Tagging and Filing Status are one section, "Compliance & Filing", built the same way as
 * Adjustments & Reports: one menu entry (opens the first screen, highlighted on all four) and one navigation bar across the top of each screen,
 * in the client's order. The routes themselves are unchanged. A client that files nothing at Companies House (sole trader, ordinary
 * partnership) has no iXBRL Tagging or Filing Status, so its section holds the first two only (see complianceFilingItemsFor).
 */
export const COMPLIANCE_FILING_LABEL = 'Compliance & Filing';
export const COMPLIANCE_FILING_ITEMS: SideMenuItem[] = [
  item('statutory-database', 'rule', 'Statutory Disclosures'),
  item('validation-centre', 'verified', 'Validation Centre'),
  item('ixbrl-facts', 'sell', 'iXBRL Tagging'),
  item('filing-status', 'assured_workload', 'Filing Status'),
];

/** The screens of the iXBRL tagging and the filing tracking: only a client that files accounts at Companies House has them. */
const FILING_ROUTES = [COMPLIANCE_FILING_ITEMS[2].to, COMPLIANCE_FILING_ITEMS[3].to];

/** The section's screens that apply to a client: all four, or without the two filing screens when the client files nothing at Companies House. */
export const complianceFilingItemsFor = (filesNothingAtCompaniesHouse: boolean): SideMenuItem[] =>
  filesNothingAtCompaniesHouse
    ? COMPLIANCE_FILING_ITEMS.filter((i) => !FILING_ROUTES.includes(i.to))
    : COMPLIANCE_FILING_ITEMS;

/** APT346: Import Exceptions is opened from the Trial Balance screen (its button beside Add Trial Balance), not from the menu. */
export const IMPORT_EXCEPTIONS_ROUTE = `${base}/tasks-importexceptions`;

export const TASK_MENU: SideMenuItem[] = [
  // APT366: the read-only trial balance view (/tasks-viewtrialbalance/<id>) keeps the Trial Balance item highlighted.
  { ...item('tasks-trialbalance', 'description', 'Trial Balance'), alsoActiveFor: [IMPORT_EXCEPTIONS_ROUTE, `${base}/tasks-viewtrialbalance`] },
  item('tasks-submit', 'send', 'Submit'),
  {
    ...item('tasks-nominalmapping', 'account_tree', ADJUSTMENTS_REPORTS_LABEL),
    alsoActiveFor: ADJUSTMENTS_REPORTS_ITEMS.map((i) => i.to),
  },
  {
    ...COMPLIANCE_FILING_ITEMS[0],
    label: COMPLIANCE_FILING_LABEL,
    alsoActiveFor: COMPLIANCE_FILING_ITEMS.map((i) => i.to),
  },
  item('accounts-builder', 'auto_stories', 'Accounts Builder'),
  item('tasks-reviewpoints', 'rate_review', 'Review Points'),
  item('tasks-approval', 'how_to_reg', 'Approval & Signatories'),
  item('tax-handoff', 'forward_to_inbox', 'Tax Handoff'),
  item('tasks-profitallocation', 'pie_chart', 'Profit Allocation'),
  item('tasks-partneraccounts', 'account_balance_wallet', 'Partner Accounts'),
  item('tasks-groupstructure', 'account_balance', 'Group Structure'),
  item('tasks-grouptrialbalance', 'view_column', 'Group Trial Balance'),
  item('tasks-groupjournals', 'library_books', 'Group Journals'),
];

/**
 * APT375 (client): the Reports menu keeps the frequently used core reports as their own entries and puts the management-period features under
 * one parent, "Management Reports". Its entry opens the first of them (Management Periods) and stays highlighted on all of them; each screen
 * shows the same top bar (ManagementReportsNav): Management Periods, Periodic Trial Balance, Budgets, Management Packs. Dimensions is a
 * smaller configuration option inside that screen, at the right of the bar, not a report of its own. The routes themselves are unchanged.
 */
export const MANAGEMENT_REPORTS_LABEL = 'Management Reports';
export const MANAGEMENT_REPORTS_ITEMS: SideMenuItem[] = [
  item('reports-managementperiods', 'calendar_month', 'Management Periods'),
  item('reports-periodictrialbalance', 'assessment', 'Periodic Trial Balance'),
  item('reports-budgets', 'savings', 'Budgets'),
  item('reports-managementpacks', 'folder_copy', 'Management Packs'),
];
export const MANAGEMENT_REPORTS_SECONDARY: SideMenuItem[] = [item('reports-dimensions', 'category', 'Dimensions')];

export const REPORTS_MENU: SideMenuItem[] = [
  item('reports-accounts', 'event_note', 'Annual Accounts'),
  item('reports-cashflows', 'payments', 'Cash Flows'),
  item('reports-trialbalance', 'description', 'Trial Balance'),
  item('reports-ladger', 'sticky_note_2', 'Nominal Ledger'),
  item('reports-managementaccounts', 'badge', 'Management Accounts'),
  {
    ...MANAGEMENT_REPORTS_ITEMS[0],
    icon: 'query_stats',
    label: MANAGEMENT_REPORTS_LABEL,
    alsoActiveFor: [...MANAGEMENT_REPORTS_ITEMS, ...MANAGEMENT_REPORTS_SECONDARY].map((i) => i.to),
  },
];

export const INTEGRATION_MENU: SideMenuItem[] = [
  item('integrations-companyofficers', 'local_police', 'Company Officers'),
  item('integrations-persons', 'person_outline', 'Per. with Sig. Control'),
  item('integrations-history', 'history', 'Filing History'),
];

export const LOGS_MENU: SideMenuItem[] = [
  item('emaillogs', 'mail', 'Email Log'),
  item('userlogs', 'person_outline', 'User Log'),
];

/**
 * APT370: Revised Accounts and Revised Accounts Lineage are one section, "Revised Accounts". Its menu entry opens the first and stays
 * highlighted on both; each of the two screens shows the same two-tab bar across the top of its content (RevisedAccountsNav), built like
 * the Adjustments & Reports and Compliance & Filing bars. The routes themselves are unchanged.
 */
export const REVISED_ACCOUNTS_LABEL = 'Revised Accounts';
export const REVISED_ACCOUNTS_ITEMS: SideMenuItem[] = [
  item('revised-accounts', 'edit_note', 'Revised Accounts'),
  item('revisions-lineage', 'history', 'Revised Accounts Lineage'),
];

/** #200: labelled "Auditor’s Report" in the menu when the client's accounts are audited (SettingsMenu). */
export const ACCOUNTANT_REPORT_MENU_ITEM = item('accountant-report', 'assignment_ind', 'Accountant’s Report');

/** BUG-062: a community interest company's own report list and its Companies House submission wizard. */
export const CIC_REPORTS_MENU_ITEM = item('reports-cicreports', 'volunteer_activism', 'CIC Reports');
export const CIC_SUBMISSION_MENU_ITEM = item('tasks-cicsubmission', 'folder_zip', 'CIC Submission Wizard');

/**
 * BUG-072: the LLP's own Report Settings page is "LLP Info" (the old application's one page with Edit and the Member's
 * Interest tick). It is the Company Info page under its LLP name, so an LLP has no separate "Company Info" item; the
 * `llp-info` URL of the earlier build still opens it.
 */
export const LLP_INFO_MENU_ITEM = item('llp-info', 'business', 'LLP Info');

/**
 * The pages of the Report Settings section. APT337: everything the old Report Settings page held (report names, size, taxonomy,
 * statement names, disclosures, currency, trading and audit status) is on Company Info, which reads and writes the same record; the old
 * `/home/taxreturns/report-setting` URL redirects there (see tr-reportsettings/screens.tsx). APT371: the section is ONE Settings-menu entry,
 * "Report Settings", and these pages are the top bar across each of them (ReportSettingsNav) - Revised Accounts is its own entry (APT372).
 */
export const REPORT_SETTINGS_SUBMENU: SideMenuItem[] = [
  item('settings-reportsettings', 'business', 'Company Info'),
  item('company-contact', 'contacts', 'Company Contacts'),
  item('director-report', 'supervisor_account', 'Director’s Report'),
  item('share-capital', 'donut_small', 'Share Capital'),
  ACCOUNTANT_REPORT_MENU_ITEM,
  item('accounting-policy', 'policy', 'Accounting Policy'),
  item('additional-notes', 'note_add', 'Additional Notes'),
  item('customise-headings', 'title', 'Customise Headings'),
];

/**
 * BUG-069/072/078: the Report Settings group for each client type. A sole trader / partnership has business (not company)
 * pages and no director's report or share capital; an LLP has a Members' Report, LLP Info (not Company Info) and no share capital.
 */
export function reportSettingsMenuFor(clientType: number): SideMenuItem[] {
  const unincorporated = clientType === 1 || clientType === 2;
  const llp = clientType === 3;
  if (!unincorporated && !llp) return REPORT_SETTINGS_SUBMENU;

  const items: SideMenuItem[] = [];
  for (const entry of REPORT_SETTINGS_SUBMENU) {
    const route = entry.to.slice(entry.to.lastIndexOf('/') + 1);
    if (route === 'share-capital') continue;
    if (unincorporated && route === 'director-report') continue;
    if (route === 'settings-reportsettings') {
      items.push({ ...entry, label: unincorporated ? 'Business Info' : llp ? 'LLP Info' : 'Company Info' });
      continue;
    }
    if (route === 'company-contact') {
      items.push({ ...entry, label: unincorporated ? 'Business Contacts' : 'LLP Contacts' });
      continue;
    }
    if (llp && route === 'director-report') {
      items.push({ ...entry, label: 'Members’ Report' });
      continue;
    }
    items.push(entry);
  }
  return items;
}

export const CIC34_MENU_ITEM = item('cic-34-report', 'summarize', 'CIC34 Report');

/** The URLs (last segment) of the Report Settings pages: the Settings menu's Report Settings entry is active on them and they show its top bar. */
export const REPORT_SETTINGS_ROUTES = [
  ...REPORT_SETTINGS_SUBMENU.map((i) => i.to),
  LLP_INFO_MENU_ITEM.to,
  CIC34_MENU_ITEM.to,
  'cic34-report',
  'cic-34-report',
].map((to) => to.slice(to.lastIndexOf('/') + 1));

/**
 * APT371 (client): "Move all Report Settings options into the top row/grid selection displayed after the user selects Report Settings, in the
 * same layout, navigation and styling as the other areas of the module (under all client types)." The Settings menu has one "Report Settings"
 * entry that opens Company Info (LLP Info / Business Info by client type) and stays highlighted on every page of the section; each page shows
 * the bar of the section's pages for that client type (reportSettingsMenuFor, plus CIC34 Report for a community interest company).
 */
export const REPORT_SETTINGS_LABEL = 'Report Settings';
export const REPORT_SETTINGS_MENU_ITEM: SideMenuItem = {
  ...item('settings-reportsettings', 'settings_applications', REPORT_SETTINGS_LABEL),
  alsoActiveFor: REPORT_SETTINGS_ROUTES.map((route) => `${base}/${route}`),
};

/**
 * APT372 (client): "Rearrange the settings options as below": Accounting Periods, Report Format, Report Settings, Revised Accounts, Chart of
 * Accounts, Fixed Asset Register, Data Security. Regulatory Pack Upgrade is not in the client's list, so it stays last.
 */
export const SETTINGS_MENU: SideMenuItem[] = [
  item('tasks-accountingperiods', 'date_range', 'Accounting Periods'),
  item('settings-reportformat', 'text_format', 'Report Format'),
  REPORT_SETTINGS_MENU_ITEM,
  { ...REVISED_ACCOUNTS_ITEMS[0], alsoActiveFor: [REVISED_ACCOUNTS_ITEMS[1].to] },
  item('settings-accoununts', 'article', 'Chart of Accounts'),
  item('fixed-asset-sync', 'precision_manufacturing', 'Fixed Asset Register'),
  item('settings-datasecurity', 'verified_user', 'Data Security'),
  item('regulatory-pack-upgrade', 'rule_folder', 'Regulatory Pack Upgrade'),
];
